import { SKILL_LEVEL_LABELS } from "./types"
import type { CurriculumItem, EmployeeProfile, SkillLevel, SkillSource } from "./types"

/**
 * Deterministic rationale for a curriculum item.
 *
 * This is the FALLBACK, and it is deliberately good enough to demo on its own:
 * the LLM step (step 6) improves the wording, but the demo must never depend on
 * a reachable backend to explain itself. A "why this?" that renders as a spinner
 * is the fastest way to lose a room.
 *
 * The rule the wording follows: name the *specific* trigger, never a generic
 * one. "Per your review: sustainability" lands; "fits your profile" does not.
 *
 * STILL PURE. Language enters as a `strings` parameter rather than as an import
 * from the i18n layer — the engine must keep compiling and testing with no
 * framework, no dictionary, and no React in scope. The caller supplies the
 * words; the engine decides which ones are true.
 */

export interface RationaleStrings {
    mandatory: string
    dueBy: string
    prerequisite: string
    closesGoal: string
    closesAlso: string
    closes: string
    fallback: string
    source: Record<SkillSource, string>
    level: Record<SkillLevel, string>
    formatDate: (iso: string) => string
    /** Optional translations for the obligation naming, keyed by id. */
    obligation?: (id: string, fallback: { label: string; basis: string }) => { label: string; basis: string }
    /** Optional translation for skill labels, keyed by id. */
    skill?: (skillId: string, fallback: string) => string
    /** Optional translation for the employee's role label. */
    role?: string
}

/** The original German wording, so existing callers and tests need no dictionary. */
export const DE_RATIONALE: RationaleStrings = {
    mandatory: "Pflicht für Ihre Funktion als {role}: {obligation} ({basis}).",
    dueBy: "Fällig bis {date}.",
    prerequisite:
        "Voraussetzung — ohne diesen Baustein können Sie die darauf aufbauenden Inhalte nicht belegen.",
    closesGoal: "Zahlt auf Ihr Entwicklungsziel ein",
    closesAlso: "Schliesst zusätzlich",
    closes: "Schliesst",
    fallback: "Empfohlen aufgrund der Anforderungen Ihrer Funktion als {role}.",
    source: {
        stellenprofil: "Laut Stellenprofil",
        lernhistorie: "Laut Lernhistorie",
        cv: "Laut Lebenslauf",
        mag: "Laut Ihrer letzten Beurteilung",
        selbsteinschaetzung: "Laut Selbsteinschätzung",
    },
    level: SKILL_LEVEL_LABELS,
    formatDate: (iso) => {
        const [year, month, day] = iso.split("-")
        return `${day}.${month}.${year}`
    },
}

function interpolate(template: string, values: Record<string, string>): string {
    return template.replace(/\{(\w+)\}/g, (match, key: string) => values[key] ?? match)
}

export function buildRationale(
    item: CurriculumItem,
    employee: EmployeeProfile,
    strings: RationaleStrings = DE_RATIONALE,
): string {
    const sentences: string[] = []
    const role = strings.role ?? employee.roleLabel
    const skillName = (skillId: string, fallback: string) =>
        strings.skill ? strings.skill(skillId, fallback) : fallback

    if (item.reason === "pflicht" && item.dischargesObligation) {
        const raw = item.dischargesObligation
        const named = strings.obligation
            ? strings.obligation(raw.id, { label: raw.label, basis: raw.basis })
            : { label: raw.label, basis: raw.basis }
        sentences.push(
            interpolate(strings.mandatory, {
                role,
                obligation: named.label,
                basis: named.basis,
            }),
        )
        if (item.dueBy) {
            sentences.push(interpolate(strings.dueBy, { date: strings.formatDate(item.dueBy) }))
        }
    }

    if (item.reason === "voraussetzung") {
        sentences.push(strings.prerequisite)
    }

    const namedGaps = item.closesGaps.slice(0, 3)
    if (namedGaps.length > 0) {
        const parts = namedGaps.map((gap) => {
            const from = strings.level[gap.current]
            const to = strings.level[gap.required]
            return `${skillName(gap.skillId, gap.skillLabel)} (${from} → ${to})`
        })
        const lead =
            item.reason === "ziel"
                ? strings.closesGoal
                : item.reason === "pflicht"
                  ? strings.closesAlso
                  : strings.closes
        sentences.push(`${lead}: ${parts.join(", ")}.`)
    }

    // Evidence is the difference between a recommendation and an argument.
    const withEvidence = item.closesGaps.find((gap) => gap.evidence && gap.evidenceSource)
    if (withEvidence?.evidence) {
        sentences.push(`${strings.source[withEvidence.evidenceSource!]}: „${withEvidence.evidence}“`)
    }

    if (sentences.length === 0) {
        sentences.push(interpolate(strings.fallback, { role }))
    }

    return sentences.join(" ")
}

export function formatDate(iso: string): string {
    return DE_RATIONALE.formatDate(iso)
}
